<template>
  <div>
    <label
      v-if="label"
      :for="name"
      class="mb-1 block w-full text-xs font-medium text-background-fg/70"
    >
      {{ label }}
    </label>
    <div class="flex space-x-2">
      <div class="relative flex-1 rounded-md">
        <button
          type="button"
          :class="[
            'flex h-9 w-full flex-1 items-center justify-between rounded-md border border-background-fg/6 bg-background-fg/5 pl-3 pr-28 text-sm font-semibold focus:outline-none sm:text-sm',
            hasError
              ? 'border-error text-error placeholder:text-error focus:border-error focus:ring-error'
              : 'focus:border-primary focus:ring-primary',
          ]"
          @click="showPinDrawer"
        >
          <template v-if="isPinSet">
            <span class="text-xl leading-none"> •••• </span>
          </template>
          <template v-else> Set Pin </template>
        </button>
        <div
          class="pointer-events-none absolute inset-y-0 right-0 flex w-48 items-center justify-end px-3"
        >
          <template v-if="!isPinSet">
            <span
              class="ml-2 flex items-center text-sm font-medium text-error"
            >
              <IconExclamationCircle class="mr-2 size-4" />
              Pin not set
            </span>
          </template>
          <template v-else-if="isPinChanged">
            <span
              class="ml-2 flex items-center text-sm font-medium text-info"
            >
              <IconExclamationCircle class="mr-2 size-4" />
              Pin changed
            </span>
          </template>
        </div>
      </div>
      <button
        type="button"
        class="flex items-center justify-center rounded-lg bg-error/10 p-2 text-error"
        @click="handleChangePin(null)"
      >
        <IconX class="" />
      </button>
    </div>

    <template v-if="hasError">
      <p class="mt-1 text-xs font-medium text-error">
        {{ errorMessage }}
      </p>
    </template>
  </div>

  <BaseDrawer
    :is-open="isPinDrawerVisible"
    shrink
    @close="hidePinDrawer"
  >
    <!-- <template #title> Set Pin </template> -->
    <div class="h-full px-6">
      <div
        class="mb-2 flex flex-col items-center justify-center"
      >
        <p class="mb-2 text-sm text-background-fg/70">
          Please enter a new pin for this cashier
        </p>
        <PinInput v-model="localPinInput" />
      </div>

      <div class="mb-4 space-y-2">
        <ActionButtonPrimary
          class="w-full"
          @click="handleChangePin(localPinInput)"
        >
          Set Pin
        </ActionButtonPrimary>
      </div>
    </div>
  </BaseDrawer>
</template>

<script setup lang="ts">
// If pin is undefined, it has not been changed.
// If pin is null, it has been removed.
// If pin is string, it has been changed.
import { useField } from "vee-validate"

const props = withDefaults(
  defineProps<{
    type?: string
    modelValue?: string | null
    name: string
    label: string
    placeholder?: string
  }>(),
  {
    modelValue: null,
    type: "text",
    placeholder: "",
  }
)

const name = toRef(props, "name")

const {
  value: inputValue,
  errorMessage,
  handleChange,
  validate,
  meta,
} = useField(name, undefined, {
  initialValue: props.modelValue,
})

const localPinInput = ref<string | null>(null)

const isPinDrawerVisible = ref(false)
const showPinDrawer = () => {
  isPinDrawerVisible.value = true
}
const hidePinDrawer = () => {
  localPinInput.value = null
  isPinDrawerVisible.value = false
}

const handleChangePin = async (value: string | null) => {
  handleChange(value)
  const { valid } = await validate()

  if (valid) {
    hidePinDrawer()
  }
}

const hasError = computed(() => !!errorMessage.value)

const isPinSet = computed(() => inputValue.value)
const isPinChanged = computed(
  () => inputValue.value !== meta.initialValue
)
</script>
